import { DateTime } from 'luxon';
import { PriceDisplay } from '../classes/price-display';
import { IPriceComponent } from '../interfaces/price-component';
import { IReservation } from '../interfaces/reservation';
import {
  AnalyticsSeriesUnit,
  IAnalyticsBreakdown,
  IAnalyticsSeriesPoint,
  IBookingSummary,
} from '../interfaces/response/admin-analytics';

/**
 * Booking analytics over reservations, shared by the admin (Guests and Hosts tabs) and the
 * server (`GET admin/reservation/analytics`) so both count the same way. Every figure's
 * definition is written next to the function that computes it.
 */

export interface DateRange {
  start: DateTime;
  end: DateTime;
}

/** Whole days in a range (inclusive). */
export function rangeDays(range: DateRange): number {
  return Math.round(range.end.plus({ milliseconds: 1 }).diff(range.start, 'days').days);
}

export function inRange(at: DateTime | undefined, range: DateRange): boolean {
  return !!at && at >= range.start && at <= range.end;
}

export function fromSeconds(seconds: unknown): DateTime | undefined {
  const n = Number(seconds);
  return Number.isFinite(n) && n > 0 ? DateTime.fromSeconds(n) : undefined;
}

/**
 * When a reservation was made: its first status-history entry. Reservations
 * have no created column; the status history is the only record of it.
 */
export function bookedAt(r: IReservation): DateTime | undefined {
  const times = (r.activity ?? []).map((a) => Number(a.timestamp)).filter((t) => Number.isFinite(t) && t > 0);
  return times.length ? DateTime.fromSeconds(Math.min(...times)) : undefined;
}

export type BookingGroup = 'booked' | 'pending' | 'declined' | 'cancelled' | 'other';

/** Same grouping as the guest site (reservation-real-mapper): accepted counts as booked. */
export function bookingGroup(r: IReservation): BookingGroup {
  const history = r.activity ?? [];
  const status = String(r.status || history[history.length - 1]?.status || '').toLowerCase();
  if (['paid', 'completed', 'rated', 'accepted'].includes(status)) return 'booked';
  if (['requested', 'change', 'processing'].includes(status)) return 'pending';
  if (status === 'rejected') return 'declined';
  if (status === 'cancelled') return 'cancelled';
  return 'other';
}

export interface Money {
  /** What the guest pays: nights and extras, less discounts, plus tax. */
  gross: number;
  /** CurbNTurf's fee, net of the $5/night feedback credit (PriceDisplay.fees). */
  fees: number;
  /** What the host is paid. */
  hostNet: number;
  nights: number;
}

/** A reservation's money, from its price rows (as originally booked, not a pending change). */
export function moneyOf(r: IReservation): Money {
  const rows: IPriceComponent[] = (r.priceComponents ?? []).map((p) => ({
    ...p,
    value: Number(p.value) || 0,
    fee: p.fee === undefined || p.fee === null ? p.fee : Number(p.fee) || 0,
  }));
  if (!rows.length) return { gross: 0, fees: 0, hostNet: 0, nights: 0 };
  const p = new PriceDisplay(rows, true);
  const nights = rows.filter((row) => row.type === 'base' && !row.pendingChange).length;
  return { gross: p.guestGross || 0, fees: p.fees || 0, hostNet: p.hostNet || 0, nights };
}

const ANSWERS = ['accepted', 'paid', 'rejected'];

export function ratio(part: number, whole: number): number | null {
  return whole ? part / whole : null;
}

export function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * A request-to-book's answer: the first entry is a request, the answer is the
 * first accept/pay/reject after it. Undefined for Instant Book and unanswered requests.
 */
export function requestAnswer(r: IReservation): { accepted: boolean; hours: number } | undefined {
  const history = [...(r.activity ?? [])].sort((a, b) => Number(a.timestamp) - Number(b.timestamp));
  if (history[0]?.status !== 'requested') return undefined;
  const answer = history.slice(1).find((a) => ANSWERS.includes(String(a.status)));
  if (!answer) return undefined;
  return {
    accepted: answer.status !== 'rejected',
    hours: (Number(answer.timestamp) - Number(history[0].timestamp)) / 3600,
  };
}

/** Money, volume and host behaviour over reservations made in `range`. */
export function summarizeBookings(reservations: IReservation[], range: DateRange): IBookingSummary {
  const made = reservations.filter((r) => inRange(bookedAt(r), range));
  const booked = made.filter((r) => bookingGroup(r) === 'booked');
  const money = booked.map(moneyOf);
  const sum = (key: keyof Money) => money.reduce((total, m) => total + m[key], 0);
  const gross = sum('gross');

  const responses: number[] = [];
  let answered = 0;
  let accepted = 0;
  for (const r of made) {
    const answer = requestAnswer(r);
    if (!answer) continue;
    answered += 1;
    if (answer.accepted) accepted += 1;
    responses.push(answer.hours);
  }

  const cancelled = made.filter((r) => bookingGroup(r) === 'cancelled').length;
  const instant = booked.filter((r) => r.site?.listing?.reservationProcess === 'instant').length;

  return {
    gross,
    fees: sum('fees'),
    hostPayouts: sum('hostNet'),
    bookings: booked.length,
    nights: sum('nights'),
    averageBooking: booked.length ? gross / booked.length : 0,
    acceptanceRate: ratio(accepted, answered),
    medianResponseHours: median(responses),
    cancellationRate: ratio(cancelled, booked.length + cancelled),
    instantShare: ratio(instant, booked.length),
  };
}

export interface SeriesPoint extends Omit<IAnalyticsSeriesPoint, 'start'> {
  start: DateTime;
}

export interface Series {
  unit: AnalyticsSeriesUnit;
  points: SeriesPoint[];
}

/** Bars for a range: by day up to 14 days, by week up to 26 weeks, else by month. */
export function seriesUnit(range: DateRange): AnalyticsSeriesUnit {
  const days = rangeDays(range);
  return days <= 14 ? 'day' : days <= 26 * 7 ? 'week' : 'month';
}

/**
 * Booked reservations in `range` by the day, week (Mon–Sun) or month they were
 * made, oldest first, in the range's time zone. The first and last buckets are
 * clipped to the range, so the buckets add up to the period's totals.
 */
export function bucketSeries(reservations: IReservation[], range: DateRange): Series {
  const unit = seriesUnit(range);
  const points: SeriesPoint[] = [];
  const ends: DateTime[] = [];
  for (let start = range.start; start <= range.end; ) {
    const unitEnd = start.endOf(unit);
    const end = unitEnd < range.end ? unitEnd : range.end;
    const label = unit === 'month' ? start.startOf('month').toFormat('LLL') : start.toFormat('LLL d');
    points.push({ label, start, gross: 0, fees: 0, hostPayouts: 0, bookings: 0, nights: 0 });
    ends.push(end);
    start = end.plus({ milliseconds: 1 });
  }
  if (unit === 'month' && new Set(points.map((p) => p.label)).size < points.length) {
    for (const p of points) p.label = p.start.toFormat("LLL ''yy");
  }
  for (const r of reservations) {
    if (bookingGroup(r) !== 'booked') continue;
    const at = bookedAt(r);
    const i = at ? points.findIndex((p, j) => at >= p.start && at <= ends[j]) : -1;
    if (i < 0) continue;
    const point = points[i];
    const m = moneyOf(r);
    point.gross += m.gross;
    point.fees += m.fees;
    point.hostPayouts += m.hostNet;
    point.bookings += 1;
    point.nights += m.nights;
  }
  return { unit, points };
}

function breakdown(
  reservations: IReservation[],
  range: DateRange,
  keyOf: (r: IReservation) => string,
): IAnalyticsBreakdown[] {
  const booked = reservations.filter((r) => bookingGroup(r) === 'booked' && inRange(bookedAt(r), range));
  const groups = new Map<string, IAnalyticsBreakdown>();
  for (const r of booked) {
    const key = keyOf(r) || 'Unknown';
    const row = groups.get(key) ?? { key, bookings: 0, gross: 0, share: 0 };
    row.bookings += 1;
    row.gross += moneyOf(r).gross;
    groups.set(key, row);
  }
  return [...groups.values()]
    .map((row) => ({ ...row, share: booked.length ? row.bookings / booked.length : 0 }))
    .sort((a, b) => b.bookings - a.bookings || b.gross - a.gross);
}

/** Bookings in the period by the listing's state. */
export function byState(reservations: IReservation[], range: DateRange): IAnalyticsBreakdown[] {
  return breakdown(reservations, range, (r) => String(r.site?.listing?.address?.state ?? '').toUpperCase());
}

/** Bookings in the period by the listing's category. */
export function byCategory(reservations: IReservation[], range: DateRange): IAnalyticsBreakdown[] {
  return breakdown(reservations, range, (r) => String(r.site?.listing?.category ?? ''));
}
